import { Scene, GameObjects } from 'phaser';

export class ChargerStorageUI {
  private scene: Scene;
  private container: GameObjects.Container;
  private background: GameObjects.Arc;
  private centerTriangle: GameObjects.Triangle;
  private sideTriangles: GameObjects.Triangle[] = [];
  private movementLines: GameObjects.Line[] = [];
  private counterText: GameObjects.Text;
  private storedChargers: number = 0;
  private maxChargers: number = 5;

  constructor(scene: Scene) {
    this.scene = scene;
    this.createUI();
    this.setupEventListeners();
  }

  private createUI(): void {
    const { width, height } = this.scene.cameras.main;
    
    // Responsive sizing
    const minDimension = Math.min(width, height);
    const radius = Math.max(25, minDimension * 0.06);
    const fontSize = Math.max(10, minDimension * 0.025);
    
    const x = width - radius - 20;
    const y = radius + 20;

    this.container = this.scene.add.container(x, y);

    // Background circle
    this.background = this.scene.add.circle(0, 0, radius, 0x222222, 0.9);

    // Center triangle (main charger)
    const triangleSize = radius * 0.4;
    this.centerTriangle = this.scene.add.triangle(
      0, 0,
      0, -triangleSize,
      -triangleSize * 0.67, triangleSize * 0.67,
      triangleSize * 0.67, triangleSize * 0.67,
      0x444444
    );

    // Side triangles (stored chargers visual)
    this.createSideTriangles();

    // Movement lines (animated effect)
    this.createMovementLines();

    // Counter text
    this.counterText = this.scene.add.text(0, radius + 10, '0/5', {
      fontSize: `${fontSize}px`,
      fontFamily: 'Courier New',
      color: '#666666',
      align: 'center'
    }).setOrigin(0.5);

    this.container.add([
      this.background,
      this.centerTriangle,
      ...this.sideTriangles,
      ...this.movementLines,
      this.counterText
    ]);

    this.container.setSize(radius * 2, radius * 2);
    this.container.setInteractive({
      useHandCursor: true,
      hitArea: new Phaser.Geom.Circle(0, 0, radius),
      hitAreaCallback: Phaser.Geom.Circle.Contains
    });

    // Setup interactions with better touch support
    this.container.on('pointerdown', () => this.onContainerClicked());
    this.container.on('pointerover', () => this.onContainerHover(true));
    this.container.on('pointerout', () => this.onContainerHover(false));
    
    // Add touch feedback
    this.container.on('pointerdown', () => {
      this.background.setFillStyle(0x333333, 0.9);
    });
    
    this.container.on('pointerup', () => {
      this.updateVisual();
    });

    // Start animation
    this.startAnimation();
  }

  private createSideTriangles(): void {
    const positions = [
      { x: -15, y: -10, scale: 0.6 },
      { x: 15, y: -10, scale: 0.6 },
      { x: -18, y: 5, scale: 0.5 },
      { x: 18, y: 5, scale: 0.5 },
      { x: 0, y: -20, scale: 0.7 }
    ];

    positions.forEach(pos => {
      const triangle = this.scene.add.triangle(
        pos.x, pos.y,
        0, -8 * pos.scale,
        -6 * pos.scale, 6 * pos.scale,
        6 * pos.scale, 6 * pos.scale,
        0x333333
      );
      triangle.setAlpha(0);
      this.sideTriangles.push(triangle);
    });
  }

  private createMovementLines(): void {
    // Create lines to simulate movement/charging effect
    for (let i = 0; i < 3; i++) {
      const line = this.scene.add.line(
        -20 + (i * 20),
        15,
        -5, 0,
        5, 0,
        0x444444
      );
      line.setAlpha(0);
      this.movementLines.push(line);
    }
  }

  private setupEventListeners(): void {
    this.scene.events.on('charger-stored', (count: number) => {
      this.storedChargers = count;
      this.updateVisual();
    });

    this.scene.events.on('chargers-released', (count: number) => {
      this.storedChargers = 0;
      this.updateVisual();
      this.playReleaseAnimation();
    });

    this.scene.events.on('max-chargers-upgraded', (max: number) => {
      this.maxChargers = max;
      this.updateCounter();
    });
  }

  private onContainerClicked(): void {
    if (this.storedChargers > 0) {
      this.scene.events.emit('release-chargers');
    }
  }

  private onContainerHover(isHovering: boolean): void {
    if (isHovering && this.storedChargers > 0) {
      this.background.setFillStyle(0x333333, 0.8);
    } else {
      this.background.setFillStyle(0x222222, 0.8);
    }
  }

  private updateVisual(): void {
    // Update center triangle color
    const centerColor = this.storedChargers > 0 ? 0xff00ff : 0x444444;
    this.centerTriangle.setFillStyle(centerColor);

    // Update side triangles visibility
    this.sideTriangles.forEach((triangle, index) => {
      if (index < this.storedChargers) {
        triangle.setAlpha(1);
        triangle.setFillStyle(0xff00ff);
      } else {
        triangle.setAlpha(0);
      }
    });

    // Update movement lines
    this.movementLines.forEach((line, index) => {
      if (index < this.storedChargers) {
        line.setAlpha(0.8);
        line.setStrokeStyle(1, 0xff00ff);
      } else {
        line.setAlpha(0);
      }
    });

    this.updateCounter();
  }

  private updateCounter(): void {
    this.counterText.setText(`${this.storedChargers}/${this.maxChargers}`);
    
    const counterColor = this.storedChargers > 0 ? 0xff00ff : 0x666666;
    this.counterText.setStyle({ color: counterColor });
  }

  private startAnimation(): void {
    // Animate movement lines
    this.scene.tweens.add({
      targets: this.movementLines,
      alpha: { from: 0, to: 0.8 },
      duration: 500,
      ease: 'Sine.easeInOut',
      repeat: -1,
      yoyo: true,
      stagger: 100
    });

    // Subtle pulsing of center triangle when chargers stored
    this.scene.tweens.add({
      targets: this.centerTriangle,
      scale: { from: 1, to: 1.1 },
      duration: 1000,
      ease: 'Sine.easeInOut',
      repeat: -1,
      yoyo: true,
      paused: true
    });
  }

  private playReleaseAnimation(): void {
    // Scale up and fade out effect
    this.scene.tweens.add({
      targets: [this.centerTriangle, ...this.sideTriangles],
      scale: { from: 1, to: 1.5 },
      alpha: { from: 1, to: 0 },
      duration: 300,
      ease: 'Power2.easeOut',
      onComplete: () => {
        // Reset visual state
        this.updateVisual();
      }
    });
  }

  public setStoredChargers(count: number): void {
    this.storedChargers = Math.min(count, this.maxChargers);
    this.updateVisual();
  }

  public setMaxChargers(max: number): void {
    this.maxChargers = max;
    this.updateCounter();
  }

  public destroy(): void {
    this.scene.events.off('charger-stored');
    this.scene.events.off('chargers-released');
    this.scene.events.off('max-chargers-upgraded');
    
    this.container.destroy();
  }
}